import React, { useMemo } from "react";

import Grid from "@material-ui/core/Grid";
import { Table, TableBody } from "@material-ui/core";
import { useFormikContext } from "formik";
import styled from "styled-components/macro";

import { SectionToggleState, useSectionHelper, useSectionToggle } from "hooks";

import { SubtleBadge } from "components/Badge";
import { CollapseLabel, FormCollapse } from "components/Form";
import { Row } from "components/Layout";

import { sectionHasErrors } from "lib/form";
import { pluralize } from "lib/pluralize";

import {
  FieldConfiguration,
  FieldGroup,
  groupConfigurationsByGroup,
} from "./fieldConfigurations";
import {
  SideLoadedFieldGroupHeadingRow,
  SideLoadedFieldRow,
} from "./SideLoadedFieldRow";

/**
 * The mapped and unmapped rows are separate tables, so the columns only line up
 * if both are laid out identically.
 */
export const FieldTable = styled(Table)`
  table-layout: fixed;
  && th:first-child,
  && td:first-child {
    width: 40%;
  }
`;

const UnmappedFieldsHeading = styled.h2`
  margin-top: ${({ theme }) => theme.space[3]}px;
`;

interface SideLoadedFieldRowsProps {
  configurations: FieldConfiguration[];
  businessId?: string;
  recordId: string;
  /** Off inside a per-group collapse, where the group is already named by the header. */
  showGroupHeadings?: boolean;
}

export function SideLoadedFieldRows({
  configurations,
  businessId,
  recordId,
  showGroupHeadings = true,
}: SideLoadedFieldRowsProps) {
  let renderedGroup: FieldGroup | null = null;

  return (
    <>
      {configurations.map(configuration => {
        const { group } = configuration;
        const heading =
          showGroupHeadings && group !== renderedGroup ? group : null;
        renderedGroup = group;

        return (
          <React.Fragment key={configuration.value}>
            {heading && <SideLoadedFieldGroupHeadingRow group={heading} />}
            <SideLoadedFieldRow
              businessId={businessId}
              configuration={configuration}
              recordId={recordId}
            />
          </React.Fragment>
        );
      })}
    </>
  );
}

interface UnmappedFieldGroupCollapseProps {
  group: FieldGroup;
  configurations: FieldConfiguration[];
  businessId?: string;
  recordId: string;
  sectionToggle: SectionToggleState;
}

function UnmappedFieldGroupCollapse({
  group,
  configurations,
  businessId,
  recordId,
  sectionToggle,
}: UnmappedFieldGroupCollapseProps) {
  const { isOpen, onToggle } = useSectionHelper(sectionToggle, group);
  const formikProps = useFormikContext();

  // The submit button is disabled by validation state the user can't see while
  // this section is collapsed, so surface it on the header.
  const hasErrorBadge = sectionHasErrors(
    formikProps,
    configurations.map(configuration => `Value__${configuration.value}`),
  );

  const header = (
    <Row>
      <SubtleBadge hasErrorBadge={hasErrorBadge}>{group}</SubtleBadge>
      <CollapseLabel>
        {configurations.length} {pluralize("Field", configurations.length)}
      </CollapseLabel>
    </Row>
  );

  return (
    <FormCollapse isOpen={isOpen} onToggle={onToggle} header={header}>
      <Grid item xs={12}>
        <FieldTable size="small">
          <TableBody>
            <SideLoadedFieldRows
              configurations={configurations}
              businessId={businessId}
              recordId={recordId}
              showGroupHeadings={false}
            />
          </TableBody>
        </FieldTable>
      </Grid>
    </FormCollapse>
  );
}

interface UnmappedFieldGroupsProps {
  configurations: FieldConfiguration[];
  businessId?: string;
  recordId: string;
  /** Nothing was suggested at all, so open the first section rather than leaving the user looking at an all-collapsed form. */
  defaultToOpen: boolean;
}

export function UnmappedFieldGroups({
  configurations,
  businessId,
  recordId,
  defaultToOpen,
}: UnmappedFieldGroupsProps) {
  const groups = useMemo(
    () => groupConfigurationsByGroup(configurations),
    [configurations],
  );

  const sectionToggle = useSectionToggle(
    defaultToOpen ? groups[0]?.group ?? null : null,
  );

  if (groups.length === 0) {
    return null;
  }

  return (
    <>
      <UnmappedFieldsHeading>Unmapped Fields</UnmappedFieldsHeading>
      {groups.map(({ group, configurations: groupConfigurations }) => (
        <UnmappedFieldGroupCollapse
          key={group}
          group={group}
          configurations={groupConfigurations}
          businessId={businessId}
          recordId={recordId}
          sectionToggle={sectionToggle}
        />
      ))}
    </>
  );
}
